import { useQuery } from '../models/reactUtils'
import { PropTypes as MobxPropTypes, observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import { alpha } from '@mui/material/styles'
import PropTypes from 'prop-types'

import { AppointmentButton } from './AppointmentButton'
import DetailIcon from './DetailIcon'
import Loading from './Loading'
import { canBookLocation, mobileAppointmentSx } from './MobileLocationDetails'
import OfficeHours from './OfficeHours'
import ProviderCorrectionDialog from './ProviderCorrectionDialog'

import {
  allProviderLocationDetails,
  locationNeedsHydration,
} from '../utils/providerLocations'

import './ProviderDetailsPanel.css'

const bookingExperiences = new Set([
  'dolphin',
  'intelibly_direct',
  'manual',
  'schedule_connect',
])

const accessibilityOptions = [
  ['wheelchairAccessibleEntrance', 'Wheelchair accessible entrance'],
  ['wheelchairAccessibleParking', 'Wheelchair accessible parking'],
  ['wheelchairAccessibleRestroom', 'Wheelchair accessible restroom'],
  ['wheelchairAccessibleSeating', 'Wheelchair accessible seating'],
]

const formatDate = value => {
  if (!value) return null
  const date = new Date(value.slice(0, 10) + 'T00:00:00')
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
}

const ProviderDetailsPanel = observer(
  ({
    provider,
    onClose,
    mobile = false,
    onLocationSelect,
    onShowLocation,
    loading = false,
    error = false,
    onRetry,
  }) => {
    const { store } = useQuery()
    const [correctionOpen, setCorrectionOpen] = useState(false)
    useEffect(() => {
      if (correctionOpen) return
      const onKeyDown = event => {
        if (event.key === 'Escape') onClose()
      }
      document.addEventListener('keydown', onKeyDown)
      return () => document.removeEventListener('keydown', onKeyDown)
    }, [onClose, correctionOpen])

    useEffect(() => {
      if (!provider?.providerLocations?.length) {
        return undefined
      }
      const matchedIds = new Set(store.searchStore.searchResultLocationIds)
      provider.providerLocations.forEach(details => {
        const locationId = details.location?.id
        if (!locationId || matchedIds.has(locationId)) {
          return
        }
        if (locationNeedsHydration(details.location)) {
          store.locationStore.ensureLocationHydrated(locationId)
        }
      })
      return undefined
    }, [provider, store])

    const providerLocations = allProviderLocationDetails(provider, store)
    const singleMobileLocation =
      mobile && providerLocations.length === 1
        ? providerLocations[0].location
        : null
    const singleMobileTags = singleMobileLocation
      ? (providerLocations[0].partnerTags ??
        singleMobileLocation.partnerTags ??
        [])
      : []

    return (
      <Paper
        className={
          'provider-details' + (mobile ? ' mobile-provider-details' : '')
        }
        role="dialog"
        aria-label={provider.displayName + ' provider details'}
        sx={{
          position: 'absolute',
          top: 84,
          left: 424,
          zIndex: 1250,
          width: 408,
          maxWidth: 'calc(100vw - 440px)',
          maxHeight: 'calc(100vh - 116px)',
          borderRadius: '16px',
          bgcolor: '#fff',
          overflow: 'hidden',
          p: '16px 27px 20px',
          boxShadow: '0 4px 4px rgba(0,0,0,.25)',
          ...(mobile && {
            position: 'relative',
            left: 'auto',
            top: 'auto',
            width: '100%',
            maxWidth: 'none',
            maxHeight: 'none',
            height: 'var(--mobile-sheet-height)',
            borderRadius: 0,
            boxShadow: 'none',
            p: 0,
            zIndex: 'auto',
          }),
        }}
      >
        {!mobile && (
          <IconButton
            aria-label="Close provider details"
            title="Close provider details"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 27,
              top: 16,
              width: 36,
              height: 36,
              p: 0,
              bgcolor: '#fff',
              zIndex: 2,
              boxShadow: '0 1px 3px rgba(44,42,41,.12)',
            }}
          >
            <DetailIcon name="close" />
          </IconButton>
        )}
        <div className="details-scroll">
          {loading && <Loading message="Loading provider details..." />}
          {error && !loading && (
            <div role="alert" className="details-error">
              <span>Couldn&apos;t load all provider details.</span>
              {onRetry && (
                <Button size="small" onClick={onRetry}>
                  Retry
                </Button>
              )}
            </div>
          )}
          {mobile && (
            <IconButton
              aria-label="Back to results"
              title="Back"
              onClick={onClose}
              sx={{ alignSelf: 'flex-end', width: 44, height: 44, p: 0 }}
            >
              <DetailIcon name="back" />
            </IconButton>
          )}
          <div className="details-portrait">
            <Avatar
              alt={provider.displayName}
              src={provider.photoUrl || undefined}
              sx={{ width: 128, height: 128, bgcolor: '#d9d9d9' }}
            >
              <span />
            </Avatar>
            {provider.partnerTier?.name && (
              <div
                className="details-tier"
                title={provider.partnerTier.description || undefined}
              >
                {provider.partnerTier.name}
              </div>
            )}
          </div>
          <div className="details-stack">
            <h2>{provider.displayName}</h2>
            {provider.specialtyList && (
              <div className="details-row">
                <DetailIcon name="dentistry" />
                <span>{provider.specialtyList}</span>
              </div>
            )}
            {!!provider.languagesSpoken?.length && (
              <div className="details-row">
                <DetailIcon name="lang" />
                <span>{provider.languagesSpoken.join('\u30fb')}</span>
              </div>
            )}
            {!!singleMobileTags.length && (
              <div className="details-row">
                <DetailIcon name="network" />
                <div className="mobile-stack">
                  {singleMobileTags.filter(Boolean).map(tag => (
                    <span key={tag.id}>{tag.name}</span>
                  ))}
                </div>
              </div>
            )}
            {provider.npiNumber && (
              <div
                className="details-row"
                aria-label={'NPI ' + provider.npiNumber}
              >
                <DetailIcon name="badge" />
                <span>{provider.npiNumber}</span>
              </div>
            )}
          </div>
          {canBookLocation(singleMobileLocation) && (
            <AppointmentButton
              location={singleMobileLocation}
              provider={provider}
              sx={mobileAppointmentSx}
            />
          )}
          {provider.bio && <div className="details-bio">{provider.bio}</div>}
          <div style={{ alignSelf: 'stretch' }}>
            {providerLocations.map(details => {
              const location = details.location
              const name =
                location.displayName || location.name || location.practice?.name
              // An empty provider-location list must not imply location-wide plan acceptance.
              const tags = details.partnerTags ?? location.partnerTags ?? []
              const updated = formatDate(location.lastUpdatedAt)
              const verified = formatDate(location.lastVerifiedAt)
              const accessibilityLabels = accessibilityOptions
                .filter(([field]) => location[field] === true)
                .map(([, label]) => label)
              const hasCoordinates =
                location.lat != null &&
                location.lat !== '' &&
                location.lng != null &&
                location.lng !== '' &&
                Number.isFinite(Number(location.lat)) &&
                Number.isFinite(Number(location.lng))
              return (
                <section
                  className="details-location"
                  key={location.id}
                  aria-label={name}
                >
                  <DetailIcon name="location" />
                  <div className="details-location-content">
                    <div className="details-location-header">
                      {mobile && onLocationSelect ? (
                        <button
                          type="button"
                          className="details-location-name details-location-link"
                          onClick={() => onLocationSelect(location)}
                        >
                          {name}
                        </button>
                      ) : (
                        <span className="details-location-name">{name}</span>
                      )}
                      <Button
                        onClick={() => {
                          store.locationStore.setFocusLocation(location, {
                            openInfoWindow: false,
                          })
                          onShowLocation?.(location)
                        }}
                        disabled={!hasCoordinates}
                        title={'Show ' + name + ' on map'}
                        variant="outlined"
                        sx={theme => ({
                          height: 28,
                          minWidth: 0,
                          flexShrink: 0,
                          borderRadius: '16px',
                          borderColor: '#d9d9d9',
                          color: 'primary.main',
                          p: '0 12px 0 8px',
                          gap: '6px',
                          fontSize: 13,
                          lineHeight: '18px',
                          fontWeight: 500,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderColor: '#d9d9d9',
                          },
                        })}
                      >
                        <DetailIcon name="location" size={16} />
                        Show on map
                      </Button>
                    </div>
                    <span>
                      {[
                        location.street,
                        [location.city, location.state, location.zip]
                          .filter(Boolean)
                          .join(' '),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                    {location.phoneNumber && (
                      <Link
                        href={'tel:' + location.phoneNumber}
                        color="inherit"
                        underline="hover"
                      >
                        {location.formattedPhoneNumber || location.phoneNumber}
                      </Link>
                    )}
                    {(details.acceptingNewPatients != null ||
                      details.telehealthAvailable != null) && (
                      <div className="details-qa">
                        {details.acceptingNewPatients != null && (
                          <div className="details-qa-row">
                            <strong>Accepting new patients:</strong>
                            <span>
                              {details.acceptingNewPatients ? 'Yes' : 'No'}
                            </span>
                          </div>
                        )}
                        {details.telehealthAvailable != null && (
                          <div className="details-qa-row">
                            <strong>Telehealth appointments:</strong>
                            <span>
                              {details.telehealthAvailable ? 'Yes' : 'No'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {!!tags.length && (
                      <div className="details-plans">
                        <strong>Plans accepted here</strong>
                        {tags.filter(Boolean).map(tag => (
                          <span
                            key={tag.id}
                            title={tag.description || undefined}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {location.officeHoursSummary?.length > 0 && (
                      <div className="details-office-hours">
                        <strong>Office hours</strong>
                        <OfficeHours location={location} />
                      </div>
                    )}
                    {!!accessibilityLabels.length && (
                      <div className="details-accessibility">
                        <strong>Accessibility</strong>
                        {accessibilityLabels.map(label => (
                          <span key={label}>{label}</span>
                        ))}
                      </div>
                    )}
                    {location.practice?.isClaimed &&
                      !singleMobileLocation &&
                      bookingExperiences.has(location.bookingExperience) && (
                        <AppointmentButton
                          location={location}
                          provider={provider}
                          sx={{
                            mt: 1,
                            mb: 0,
                            height: mobile ? 44 : 40,
                            minHeight: mobile ? 44 : 40,
                          }}
                        />
                      )}
                    {updated && (
                      <span style={{ color: '#666', marginTop: 8 }}>
                        Information last updated {updated}
                      </span>
                    )}
                    {verified && (
                      <span style={{ color: '#666' }}>
                        Last verified {verified}
                      </span>
                    )}
                  </div>
                </section>
              )
            })}
          </div>
          <div className="details-correct">
            <div>Need to correct this information?</div>
            <Link
              component="button"
              type="button"
              onClick={() => setCorrectionOpen(true)}
              sx={{
                mt: '4px',
                color: '#003087',
                fontSize: 14,
                lineHeight: '20px',
                fontWeight: 500,
              }}
              underline="always"
            >
              Report an update
            </Link>
          </div>
        </div>
        {correctionOpen && (
          <ProviderCorrectionDialog
            providerId={provider.id}
            providerName={provider.displayName}
            onClose={() => setCorrectionOpen(false)}
          />
        )}
      </Paper>
    )
  }
)

ProviderDetailsPanel.displayName = 'ProviderDetailsPanel'
ProviderDetailsPanel.propTypes = {
  provider: MobxPropTypes.observableObject.isRequired,
  onClose: PropTypes.func.isRequired,
  mobile: PropTypes.bool,
  onLocationSelect: PropTypes.func,
  onShowLocation: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
}

export default ProviderDetailsPanel
